import React, { useMemo, useRef } from "react";
import type { useSessionMessages } from "@livekit/components-react";
import { BookingCard } from "../cards/booking/BookingCard";
import { validateBookingCard } from "../cards/booking/validate";
import { AvailabilityCards } from "../cards/availability/AvailabilityCard";
import { validateAvailabilityResult } from "../cards/availability/validate";
import { ProductCards } from "../cards/product/ProductCard";
import { CoachCards } from "../cards/coach/CoachCard";
import { validateCoachResult } from "../cards/coach/validate";
import { TournamentCards } from "../cards/tournament/TournamentCard";
import { validateTournamentResult } from "../cards/tournament/validate";
import { OpenPlayCards } from "../cards/open-play/OpenPlayCard";
import { validateOpenPlayResult } from "../cards/open-play/validate";
import { validateProductResult } from "../cards/product/validate";
import { CardsSkeleton } from "../cards/Skeleton";
import { CheckoutCard } from "../cards/checkout/CheckoutCard";
import { validateCheckoutCard } from "../cards/checkout/validate";
import { LessonCard } from "../cards/lesson/LessonCard";
import { validateLessonCard } from "../cards/lesson/validate";
import { CartCard } from "../cards/cart/CartCard";
import { McpAppCard } from "../cards/mcp-app/McpAppCard";
import { validateCartCard } from "../cards/cart/validate";
import type { ReceivedToolResult } from "../useToolResults";
import { isCourtneyGreeting } from "./greeting";

type Messages = ReturnType<typeof useSessionMessages>["messages"];

export type TimelineItem = {
  ts: number;
  node: React.ReactNode;
  kind?: "msg" | "card";
  // Cards only: when the tool's result arrived (see ReceivedToolResult.resultAt).
  settledAt?: number;
};

// Sorts before every real timestamp. Not -Infinity: the near-simultaneous check
// below subtracts timestamps, and Infinity - Infinity is NaN.
export const GREETING_TS = Number.MIN_SAFE_INTEGER;
const GREETING_KEY = "m-greeting";

/**
 * Chronological order of the thread. Cards and messages arrive on different
 * paths, so a message and a card within ~1.2 s of each other need a tie-break:
 * a message seen before the card's result arrived is the narration ("let me
 * check...") and goes above the card; one seen after it is her reply to the
 * results and goes below. Since text chats went text-only her reply can land
 * within 1.2 s of the skeleton, and the old "message always on top" rule put
 * it above the cards. Otherwise by timestamp.
 */
export function orderTimeline<T extends Pick<TimelineItem, "ts" | "kind" | "settledAt">>(
  items: T[],
): T[] {
  return [...items].sort((a, b) => {
    if (a.kind !== b.kind && Math.abs(a.ts - b.ts) < 1200) {
      const [msg, card] = a.kind === "msg" ? [a, b] : [b, a];
      const msgFirst = card.settledAt === undefined || msg.ts < card.settledAt;
      return (a === msg) === msgFirst ? -1 : 1;
    }
    return a.ts - b.ts;
  });
}

/**
 * The id of Courtney's opening greeting, if it is in the thread. It is the
 * conversation's first line by design, but a message the user typed while she
 * was still joining (queued, sent the moment she arrives) can reach the agent
 * before the greeting is produced; her reply, or the greeting itself, then
 * lands after it. The caller pins this message to the top so the thread reads
 * greeting first, then what the user said, then her answers. Only the first
 * greeting counts: a later identical line is an ordinary reply.
 */
export function findGreetingId(
  messages: readonly { id: string; type?: string; message: string; from?: { identity?: string } }[],
  localIdentity: string,
): string | undefined {
  return messages.find(
    (m) =>
      m.type !== "userTranscript" &&
      !(m.type === "chatMessage" && m.from?.identity === localIdentity) &&
      isCourtneyGreeting(m.message),
  )?.id;
}

function renderToolCard(result: ReceivedToolResult): React.ReactNode {
  // Any tool can emit a `{loading:true}` frame at call-start → shared skeleton,
  // replaced in place when the real result frame lands.
  if ((result.data as Record<string, unknown>)?.loading === true) {
    return <CardsSkeleton tool={result.tool} />;
  }
  // A CourtsApp MCP tool that ships its own MCP Apps HTML card: show that card
  // (same one ChatGPT/Claude show). Our React card for the tool, if any, is the
  // fallback when the HTML can't load.
  if (result.ui) {
    return (
      <McpAppCard
        tool={result.tool}
        ui={result.ui}
        fallback={renderToolCard({ ...result, ui: undefined })}
      />
    );
  }
  if (result.tool === "get_booking_link") {
    const data = validateBookingCard(result.data);
    return data ? <BookingCard data={data} /> : null;
  }
  if (result.tool === "search_availability") {
    const data = validateAvailabilityResult(result.data);
    if (!data || data.facilities.length === 0) return null;
    return <AvailabilityCards facilities={data.facilities} />;
  }
  if (result.tool === "search_open_play") {
    const facilities = validateOpenPlayResult(result.data);
    return facilities.length > 0 ? <OpenPlayCards facilities={facilities} /> : null;
  }
  if (result.tool === "search_products") {
    const data = validateProductResult(result.data);
    if (!data || data.products.length === 0) return null;
    return <ProductCards products={data.products} />;
  }
  if (result.tool === "find_lesson_coaches") {
    const data = validateCoachResult(result.data);
    if (!data || data.coaches.length === 0) return null;
    return <CoachCards coaches={data.coaches} />;
  }
  if (result.tool === "find_tournaments") {
    const data = validateTournamentResult(result.data);
    if (!data || data.tournaments.length === 0) return null;
    return <TournamentCards tournaments={data.tournaments} />;
  }
  if (result.tool === "send_lesson_booking_link") {
    const data = validateLessonCard(result.data);
    return data ? <LessonCard data={data} /> : null;
  }
  if (result.tool === "create_checkout") {
    const data = validateCheckoutCard(result.data);
    return data ? <CheckoutCard data={data} /> : null;
  }
  if (result.tool === "add_to_cart") {
    const data = validateCartCard(result.data);
    if (!data) return null;
    if (data.items.length === 1) {
      const item = data.items[0];
      return (
        <CheckoutCard
          data={{
            productTitle: item.productTitle,
            brand: data.brand,
            variantTitle: item.variantTitle ?? null,
            imageUrl: item.imageUrl ?? null,
            quantity: item.quantity,
            totalAmount: data.totalAmount,
            currency: data.currency,
            checkoutUrl: data.checkoutUrl,
            storeDomain: data.storeDomain,
          }}
        />
      );
    }
    return <CartCard data={data} />;
  }
  return null;
}

function formatDisplay(text: string): string {
  // Collapse spaced-out phone numbers: "5 1 6 - 8 5 3 - 7 5 1 9" → "(516) 853-7519"
  let result = text.replace(
    /(\d)\s(\d)\s(\d)\s-\s(\d)\s(\d)\s(\d)\s-\s(\d)\s(\d)\s(\d)\s(\d)/g,
    "($1$2$3) $4$5$6-$7$8$9$10",
  );
  // Collapse dashed zip codes: "1-1-0-5-0" → "11050"
  result = result.replace(
    /\b(\d)-(\d)-(\d)-(\d)-(\d)\b/g,
    "$1$2$3$4$5",
  );
  // "199 dollars" → "$199"
  result = result.replace(/\b(\d[\d,]*)\s*dollars?\b/gi, "$$$1");
  return result;
}

function AgentBubble({
  children,
  avatarUrl,
}: {
  children: React.ReactNode;
  avatarUrl?: string;
}) {
  return (
    <div className="lk-widget-msg-agent-row">
      {avatarUrl && (
        <div
          className="lk-widget-msg-agent-avatar"
          style={{ backgroundImage: `url(${avatarUrl})` }}
          role="img"
          aria-label="Courtney"
        />
      )}
      <div className="lk-widget-msg-agent-bubble">{children}</div>
    </div>
  );
}

export function useConversationTimeline({
  messages,
  toolResults,
  localIdentity,
  userInitials,
  avatarUrl,
  localGreeting,
  greetingDisplay,
}: {
  messages: Messages;
  toolResults: ReceivedToolResult[];
  localIdentity: string;
  userInitials?: string | null;
  avatarUrl?: string;
  // Greeting the widget shows before Courtney has joined (see greeting.ts).
  // Dropped once her own greeting is in the thread; both use the same key, so
  // React keeps the bubble in place instead of remounting it.
  localGreeting?: string | null;
  // While the greeting animation plays (typedGreeting.ts): "" hides the
  // greeting bubble (typing dots are up), any other string is the text shown
  // so far. Applies to her real greeting too, so an early arrival doesn't jump
  // to full text. Undefined: show the greeting normally.
  greetingDisplay?: string;
}): TimelineItem[] {
  // Client first-seen time per message id. Messages carry a SERVER timestamp
  // while tool cards carry a CLIENT receive time — comparing the two across
  // clocks is what occasionally flipped a message below its cards. Stamping each
  // message with the client time we first saw it puts both on one clock.
  const seenRef = useRef<Map<string, number>>(new Map());
  return useMemo(() => {
    const greetingId = findGreetingId(messages, localIdentity);
    return orderTimeline<TimelineItem>([
        ...messages.flatMap<TimelineItem>((msg) => {
          if (msg.id === greetingId && greetingDisplay === "") return [];
          const isUser =
            msg.type === "userTranscript" ||
            (msg.type === "chatMessage" && msg.from?.identity === localIdentity);
          let seen = seenRef.current.get(msg.id);
          if (seen === undefined) {
            seen = Date.now();
            seenRef.current.set(msg.id, seen);
          }
          return [{
            ts: msg.id === greetingId ? GREETING_TS : seen,
            kind: "msg",
            node: (
              <div
                key={msg.id === greetingId ? GREETING_KEY : `m-${msg.id}`}
                className={`lk-widget-msg ${isUser ? "lk-widget-msg--user" : "lk-widget-msg--agent"}`}
              >
                {isUser ? (
                  <div className="lk-widget-msg-row">
                    <div className="lk-widget-msg-bubble">{msg.message}</div>
                    {userInitials && (
                      <span
                        className="lk-widget-msg-initials"
                        aria-hidden="true"
                      >
                        {userInitials}
                      </span>
                    )}
                  </div>
                ) : (
                  <AgentBubble avatarUrl={avatarUrl}>
                    {formatDisplay(
                      msg.id === greetingId && greetingDisplay !== undefined ? greetingDisplay : msg.message,
                    )}
                  </AgentBubble>
                )}
              </div>
            ),
          }];
        }),
        ...(localGreeting && !greetingId && greetingDisplay !== ""
          ? [
              {
                ts: GREETING_TS,
                kind: "msg" as const,
                node: (
                  <div key={GREETING_KEY} className="lk-widget-msg lk-widget-msg--agent">
                    <AgentBubble avatarUrl={avatarUrl}>{formatDisplay(greetingDisplay ?? localGreeting)}</AgentBubble>
                  </div>
                ),
              },
            ]
          : []),
        // Hide cards until the agent replies past the result — otherwise the
        // card lands bare while the agent is still composing its response.
        ...toolResults.flatMap<TimelineItem>((result) => {
          // Cards show immediately: a skeleton frame establishes the slot at
          // call-start and the real result swaps in place, so nothing ever
          // "lands bare" — no need to gate on the agent's follow-up reply.
          const card = renderToolCard(result);
          if (!card) return [];
          const cardClass = result.exiting
            ? "lk-widget-msg lk-widget-msg--card lk-widget-msg--card-exiting"
            : "lk-widget-msg lk-widget-msg--card";
          return [
            {
              ts: result.receivedAt,
              kind: "card" as const,
              settledAt: result.resultAt,
              node: (
                <div
                  key={`c-${result.tool}-${result.receivedAt}`}
                  className={cardClass}
                >
                  {card}
                </div>
              ),
            },
          ];
        }),
    ]);
  }, [messages, toolResults, localIdentity, userInitials, avatarUrl, localGreeting, greetingDisplay]);
}
